// Modal.tsx
import React, { useEffect, useState } from 'react';
import { Button, DropDown } from '../../Atoms';
import CustomizedDatePicker from '../../Atoms/DatePicker';
import InputLabel from '../../Atoms/InputLabel';
import TimePicker from '../../Atoms/TimeInput';
import { ListTypes, POATypes, QuestionnaireResponseStatus } from '@/constants/enums';
import dayjs from 'dayjs';
import { IPoaModalItem } from '@/components/survey/SurveyModel/types/nodeTypes';
import { useUpdatePoaType } from '@/state/survey/poaSurveySlice';

interface TriageListModalProps {
    isOpen: boolean;
    onClose: (poaModalItem: IPoaModalItem | null) => void;
    modalSelection: number;
    name: string;
    questionnaireResponseId: number;
    waitingListRecordId: number;
    existingPOADateTime: Date;
    submitButtonText: string;
    type: ListTypes;
}

const TriageListModal: React.FC<TriageListModalProps> = ({
    isOpen,
    onClose,
    modalSelection,
    name,
    questionnaireResponseId,
    waitingListRecordId,
    existingPOADateTime,
    submitButtonText
}) => {
    const [poaDate, setPoaDate] = useState<string>('');
    const [poaTime, setPoaTime] = useState<string | Date>('');
    const [selectedPOA, setSelectedPOA] = useState<{ value: number; label: string } | null>(null);
    const updatePoaStatusDispatch = useUpdatePoaType();

    const getPOALabel = (poaType: number) => {
        switch (poaType) {
            case POATypes.PhoneCall:
                return 'Phone call';
            case POATypes.FaceToFace:
                return 'Face to face';
            default:
                return 'Unknown';
        }
    };

    // Initialize modal selection and date/time
    useEffect(() => {
        if (modalSelection != null) {
            setSelectedPOA({ value: modalSelection, label: getPOALabel(modalSelection) });
        }

        if (existingPOADateTime && !isNaN(existingPOADateTime.getTime())) {
            const date = dayjs(existingPOADateTime);
            setPoaDate(date.format('YYYY-MM-DD'));
            setPoaTime(date.toISOString());
        }
    }, [modalSelection, existingPOADateTime]);

    const handleDropdownChange = (selectedOption: { value: number; label: string } | null) => {
        setSelectedPOA(selectedOption);
    };

    const enableSubmitButton = !!poaDate && !!poaTime && !!selectedPOA;

    const handleSubmit = async () => {
        if (!enableSubmitButton) return;

        try {
            if (!poaDate || !poaTime || !selectedPOA) return;

            // Parse date and time
            const datePart = dayjs(poaDate);
            let timePart = dayjs();

            if (typeof poaTime === 'string') {
                if (poaTime.includes('T')) {
                    timePart = dayjs(poaTime);
                } else {
                    const [hours, minutes] = poaTime.split(':');
                    timePart = dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
                }
            } else if (poaTime instanceof Date) {
                timePart = dayjs(poaTime);
            }

            const combinedDate = datePart
                .hour(timePart.hour())
                .minute(timePart.minute())
                .second(0)
                .toDate();

            const valuesToUpdate = [
                { "op": "replace", "path": "/POAType", "value": selectedPOA.value },
                { "op": "replace", "path": "/POADateTime", "value": combinedDate },
                { "op": "replace", "path": "/StatusId", "value": QuestionnaireResponseStatus.POA}
            ];

            await updatePoaStatusDispatch(
                waitingListRecordId, 
                questionnaireResponseId, 
                valuesToUpdate, 
                ListTypes.PoaList
            );

            onClose({
                id: waitingListRecordId,
                poaDateTime: combinedDate.toISOString(),
                poaType: selectedPOA.value as POATypes
            });

        } catch (error) {
            console.error("Error updating POA:", error);
            onClose(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={() => onClose(null)}>
            <div className="modal-content modal-content-triage" onClick={(e) => e.stopPropagation()}>
                <div className='modal-form'>
                    <h1 className='modal-header'>{name}</h1>
                    <p className='poa-appointment-instruction'>
                        Enter the POA appointment type, date and time as shown in TrakCare
                    </p>

                    <div className={`user-input drop-down user-input-triage`}>
                        <InputLabel label={'Type of POA'} labelClasses="input-label"></InputLabel>
                        <div className="drop-down-field">
                            <DropDown
                                propDivClasses={'custom-select-triage'}
                                handleChange={handleDropdownChange}
                                options={[
                                    { value: POATypes.PhoneCall, label: 'Phone call' },
                                    { value: POATypes.FaceToFace, label: 'Face to face' }
                                ]}
                                value={selectedPOA}
                                isInValid={false}
                            />
                        </div>
                    </div>

                    <div className='row-container'>
                        <div className="user-input-wrapper input-date user-input-triage">
                            <InputLabel label={'Date of POA'} labelClasses="input-label" />
                            <CustomizedDatePicker
                                onChange={(date: string | null) => setPoaDate(date || '')}
                                initialDate={poaDate}
                            />
                        </div>

                        <div className="user-input-wrapper input-time user-input-triage">
                            <InputLabel label={'Time'} labelClasses="input-label" />
                            <TimePicker
                                onChange={(time: any) => setPoaTime(time)}
                                initialTime={typeof poaTime === 'string' ? poaTime : dayjs(poaTime).format('YYYY-MM-DDTHH:mm:ssZ')}
                            />
                        </div>
                    </div>

                    <div className='button-container button-container-poa-list'>
                        <Button 
                            type='tertiary'
                            text={submitButtonText} 
                            icon={false} 
                            handleClick={handleSubmit} 
                            stateProp={'enabled'} 
                        />
                        <Button 
                            type='primary' 
                            text="Cancel" 
                            icon={false} 
                            handleClick={() => onClose(null)} 
                            stateProp='enabled' 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TriageListModal;